import { useTranslation } from "react-i18next";
import { ClientError } from "../../../../../api/src/shared/request";
import { displayToast } from "../../../components/app/AppToast";
import {
  apiClient,
  clearApiQuery,
  resetApiQuery,
  useApiRevalidate,
  type Api,
} from "../../../services/api";
import {
  conversationIdEngine,
  type ConversationEngine,
} from "../../assistant/conversationEngine";

// The two owner actions on a conversation, next to `useShareConversation`: one
// call, one toast, one cache update. Both resolve `false` (the delete: `null`)
// on failure instead of throwing, so the menu that called them keeps its sheet
// open on the value the user typed rather than closing over a write that did
// not land. The id says which engine owns the conversation.

/** Give the conversation the title the owner typed. */
export function useRenameConversation(): (
  conversationId: string,
  title: string
) => Promise<boolean> {
  const { t } = useTranslation();
  const revalidate = useApiRevalidate();

  return async (conversationId, title) => {
    try {
      // The list key covers BOTH the history drawer's infinite query and any
      // regular query on the same path (`.knowledge/react-query-infinite-vs-regular-key-collision.md`),
      // and the detail key repaints the open conversation's own header title.
      // A continuation and its origin share the title: the response names the other one.
      if (conversationIdEngine(conversationId) === "assistant") {
        const { body } = await apiClient.patch(
          "/assistant/conversations/:conversationId",
          { param: { conversationId }, json: { title } }
        );
        await revalidate("/assistant/conversations");
        await revalidate("/assistant/conversations/:conversationId", {
          param: { conversationId },
        });
        if (body.legacyConversationId !== null) {
          await revalidate("/webapp/private/conversations");
          await revalidate("/webapp/private/conversations/:id", {
            param: { id: body.legacyConversationId },
          });
        }
        return true;
      }
      const { body } = await apiClient.patch(
        "/webapp/private/conversations/:id",
        { param: { id: conversationId }, json: { title } }
      );
      await revalidate("/webapp/private/conversations");
      // The assistant's history lists the legacy rows too.
      await revalidate("/assistant/conversations");
      await revalidate("/webapp/private/conversations/:id", {
        param: { id: conversationId },
      });
      if (body.continuationId !== null)
        await revalidate("/assistant/conversations/:conversationId", {
          param: { conversationId: body.continuationId },
        });
      return true;
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return false;
    }
  };
}

/**
 * Delete the conversation and the bytes of the files attached to it.
 *
 * The cache is CLEARED, not revalidated, and both halves of that matter:
 *
 *  - The transcript must go. Query data is persisted to localStorage with an
 *    infinite `gcTime`, and both the detail response and the LIST response carry
 *    the whole transcript. Marking them stale leaves the clinical questions the
 *    user just deleted sitting on the device until something happens to overwrite
 *    them, which is not what "permanently deleted" says on the confirmation.
 *  - Refetching the list in place would skip a row. The endpoint pages by
 *    `offset`, so once a conversation is gone every row after it shifts down one;
 *    refetching the SAME offsets re-reads page 2 from the old boundary and the
 *    row that slid out of page 1 is in neither. Dropping the pages makes the list
 *    start again from the first one.
 */
export function useDeleteConversation(): (
  conversationId: string
) => Promise<string[] | null> {
  const { t } = useTranslation();

  // The ids deleted: the conversation and the one linked to it (a continuation
  // and its legacy origin go together), so a page showing either can leave.
  // Null when nothing was deleted.
  return async (conversationId) => {
    try {
      const deleted =
        conversationIdEngine(conversationId) === "assistant"
          ? {
              assistantId: conversationId,
              legacyId: (
                await apiClient.delete(
                  "/assistant/conversations/:conversationId",
                  { param: { conversationId } }
                )
              ).body.legacyConversationId,
            }
          : {
              legacyId: conversationId,
              assistantId: (
                await apiClient.delete("/webapp/private/conversations/:id", {
                  param: { id: conversationId },
                })
              ).body.continuationId,
            };
      // The LIST is reset, not removed: the history drawer is usually mounted on
      // it when the delete is fired from one of its rows, and `removeQueries`
      // dispatches to no observer — the drawer would keep rendering the pages it
      // already had, deleted row included. `resetQueries` drops the pages AND
      // repaints, so the row goes and the list reloads from its first page.
      //
      // The DETAIL is removed rather than reset: the conversation page may still
      // be mounted on this key for the instant before it navigates away, and a
      // reset would refetch a row that no longer exists — a 404 flashing over a
      // page that is already leaving.
      //
      // The last conversation may be the one just deleted: the landing
      // greeting must not name its subject.
      //
      // The assistant's history lists the legacy rows too.
      await resetApiQuery("/assistant/conversations", {});
      if (deleted.assistantId !== null) {
        clearApiQuery("/assistant/conversations/:conversationId", {
          param: { conversationId: deleted.assistantId },
        });
        clearApiQuery("/assistant/latest-topic", {});
      }
      if (deleted.legacyId !== null) {
        await resetApiQuery("/webapp/private/conversations", {});
        clearApiQuery("/webapp/private/conversations/:id", {
          param: { id: deleted.legacyId },
        });
        clearApiQuery("/webapp/private/conversations/latest-topic", {});
      }
      displayToast(t("Conversation deleted"), "success");
      return [deleted.assistantId, deleted.legacyId].filter(
        (id) => id !== null
      );
    } catch (error) {
      // 409 is the one refusal with a next step: the server is still writing an
      // answer (or ingesting a file) for this conversation, so "try again" is
      // wrong advice — waiting is. Anything else keeps the generic message.
      const isBusy =
        error instanceof ClientError && error.responseStatus === 409;
      displayToast(
        isBusy
          ? t("This conversation is still working. Try again in a moment.")
          : t("Something went wrong. Please try again."),
        "error"
      );
      return null;
    }
  };
}

// The most ids one bulk delete request takes (the routes' own bound).
const BULK_DELETE_MAX_IDS = 500;

/**
 * Delete several conversations at once: the rows the history page's selection
 * names, or every row its current search lists ("all", loaded on the page or
 * not). The cache is cleared for the same reasons as a single delete, and every
 * detail goes with it: the response counts the rows, it does not name them.
 *
 * A conversation still writing an answer is skipped by the server, not refused,
 * so the rest still go; the toast says how many stayed.
 */
export function useDeleteConversations(): (
  engine: ConversationEngine,
  // The two engines' bulk routes take the same body.
  deletion: Api["delete"]["/webapp/private/conversations"]["input"]["json"]
) => Promise<boolean> {
  const { t } = useTranslation();

  return async (engine, deletion) => {
    // The routes take 500 ids at most; a longer hand-picked selection goes in
    // several requests.
    const requests =
      deletion.target === "ids"
        ? Array.from(
            { length: Math.ceil(deletion.ids.length / BULK_DELETE_MAX_IDS) },
            (_, index) => ({
              target: "ids" as const,
              ids: deletion.ids.slice(
                index * BULK_DELETE_MAX_IDS,
                (index + 1) * BULK_DELETE_MAX_IDS
              ),
            })
          )
        : [deletion];
    let deleted = 0;
    let busy = 0;
    let failed = 0;
    // A request that fails may still have deleted part of its rows: they are
    // unknown, not counted as failed, and the reset list shows what is left.
    let isComplete = true;
    for (const request of requests) {
      try {
        const { body } =
          engine === "assistant"
            ? await apiClient.delete("/assistant/conversations", {
                json: request,
              })
            : await apiClient.delete("/webapp/private/conversations", {
                json: request,
              });
        deleted += body.deleted;
        busy += body.busy;
        failed += body.failed;
      } catch {
        // The next batches still go: they name other rows.
        isComplete = false;
      }
    }
    // Reset even after a failure: an earlier request may have deleted rows.
    // Both engines: the assistant's history lists legacy rows and deletes continued origins.
    await resetApiQuery("/assistant/conversations", {});
    clearApiQuery("/assistant/conversations/:conversationId", {});
    clearApiQuery("/assistant/latest-topic", {});
    await resetApiQuery("/webapp/private/conversations", {});
    clearApiQuery("/webapp/private/conversations/:id", {});
    clearApiQuery("/webapp/private/conversations/latest-topic", {});
    if (deleted > 0)
      displayToast(
        t("{{count}} conversations deleted", { count: deleted }),
        "success"
      );
    if (busy > 0)
      displayToast(
        t(
          "{{count}} conversations are still working and were kept. Try again in a moment.",
          { count: busy }
        ),
        "error"
      );
    if (failed > 0)
      displayToast(
        t("{{count}} conversations could not be deleted. Please try again.", {
          count: failed,
        }),
        "error"
      );
    if (!isComplete)
      displayToast(t("Something went wrong. Please try again."), "error");
    // The confirmation stays open only when nothing went.
    return deleted > 0 || (isComplete && failed === 0);
  };
}
